import * as THREE from 'three';

/**
 * A handful of soaring birds — dark chevrons circling thermals near the focus.
 * Pure theatre at valley scale, hidden from god height. Render-only; like the
 * clouds, no world state may ever read them.
 */
export interface Birds {
  mesh: THREE.InstancedMesh;
  update(t: number, focusX: number, focusZ: number, groundY: number, camR: number): void;
}

export function createBirds(count = 18): Birds {
  // a shallow chevron: two swept wings
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
    -0.5, 0.06, 0.02,   0, 0, -0.12,   0, 0, 0.16,
     0.5, 0.06, 0.02,   0, 0, 0.16,    0, 0, -0.12,
  ]), 3));
  geo.computeVertexNormals();
  const mat = new THREE.MeshBasicMaterial({ color: 0x22201c, side: THREE.DoubleSide });
  const mesh = new THREE.InstancedMesh(geo, mat, count);
  mesh.frustumCulled = false;

  // each bird owns a thermal: an offset circle it rides, at its own pace
  const birds = Array.from({ length: count }, (_, i) => {
    const h = (n: number) => { const x = Math.sin(i * 127.1 + n * 311.7) * 43758.5453; return x - Math.floor(x); };
    return {
      ox: (h(1) - 0.5) * 3600, oz: (h(2) - 0.5) * 3600,
      alt: 90 + h(3) * 260, R: 50 + h(4) * 190,
      w: (0.05 + h(5) * 0.11) * (h(6) > 0.5 ? 1 : -1),
      ph: h(7) * Math.PI * 2,
      span: 1.6 + h(8) * 1.6,
    };
  });

  const anchor = new THREE.Vector2(0, 0);
  let anchored = false;
  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), e = new THREE.Euler();
  const pos = new THREE.Vector3(), scl = new THREE.Vector3();

  return {
    mesh,
    update(t, focusX, focusZ, groundY, camR) {
      // birds are valley-scale life; from god height they are noise
      mesh.visible = camR < 6500;
      if (!mesh.visible) return;
      if (!anchored) { anchor.set(focusX, focusZ); anchored = true; }
      // the flock's home drifts after the focus rather than teleporting
      anchor.x += (focusX - anchor.x) * 0.006;
      anchor.y += (focusZ - anchor.y) * 0.006;

      for (let i = 0; i < birds.length; i++) {
        const b = birds[i];
        const a = t * b.w + b.ph;
        pos.set(anchor.x + b.ox + Math.cos(a) * b.R,
                groundY + b.alt + Math.sin(t * 0.31 + b.ph) * 14,
                anchor.y + b.oz + Math.sin(a) * b.R);
        // heading along the tangent, banked into the turn
        e.set(0, -a - (b.w > 0 ? 0 : Math.PI), b.w > 0 ? 0.35 : -0.35, 'YXZ');
        q.setFromEuler(e);
        const flap = 1 + Math.sin(t * 6.2 + b.ph * 3.1) * 0.16;
        scl.set(b.span * flap, b.span, b.span);
        m.compose(pos, q, scl);
        mesh.setMatrixAt(i, m);
      }
      mesh.instanceMatrix.needsUpdate = true;
    },
  };
}
